import {Alert} from 'src/design/atoms/Alert';
import {Box} from 'src/design/atoms/Box';
import {Center} from 'src/design/atoms/Center';
import {Grid} from 'src/design/atoms/Grid';
import {Group} from 'src/design/atoms/Group';
import {Loader} from 'src/design/atoms/Loader';
import {Stack} from 'src/design/atoms/Stack';
import React, {useEffect, useMemo, useState} from 'react';
import {useWatch} from 'react-hook-form';
import type {
  ComplianceStandardResponse,
  CustomComplianceStandardRequest,
} from 'src/api/ProactApiClient';
import {ModuleEnum} from 'src/api/ProactApiClient';
import {
  useCreateCustomComplianceStandardMutation,
  useGetCustomComplianceStandardQuery,
  useUpdateCustomComplianceStandardMutation,
} from 'src/redux/queries/complianceStandard';
import {toFormDataV2} from 'src/app/forms/engine/formData';
import {ProactField} from 'src/app/forms/engine/ProactField';
import {ProactForm} from 'src/app/forms/engine/ProactForm';
import {useEntitySyncedForm} from 'src/app/forms/engine/useEntitySyncedForm';
import {BackBar} from 'src/app/layout/BackBar';
import {PageHeader} from 'src/design/organisms/PageHeader';
import type {PageNavigationConfig} from 'src/app/layout/pageNavigationConfig';
import {buildPageNavigation} from 'src/app/layout/pageNavigationConfig';
import {SectionCard} from 'src/design/atoms/SectionCard';
import {SidebarLinkComplianceRegulatory} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {useModulePermissions} from 'src/shared/auth/permissions/useModulePermissions';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import type {IFormDataV2, IFormFieldV2} from 'src/types/FormV2';
import {useBackNavigation} from 'src/shared/hooks/useBackNavigation';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

const navigationConfig: PageNavigationConfig = {
  entityName: 'Company Standard',
  entityNamePlural: 'Compliance Standards',
  listPath: PathUtility.InstanceComplianceStandards('company'),
  viewPath: id => PathUtility.CustomComplianceStandardsView(id),
};

export interface CompanyStandardFormProps {
  id?: number;
  mode: 'create' | 'view' | 'edit';
}

const STANDARD_DETAILS_META: IFormFieldV2[] = [
  {
    name: 'sectionLabel',
    label: 'Standard',
    type: 'text',
    required: true,
    size: 'md',
    validation: {maxLength: 500},
    description: 'e.g., OSHA 1910.134',
  },
  {
    name: 'titleNumberLabel',
    label: 'Title',
    type: 'text',
    required: false,
    size: 'md',
    validation: {maxLength: 255},
    description: 'e.g., Title 29',
  },
];

const DATES_META: IFormFieldV2[] = [
  {
    name: 'issuedAt',
    label: 'Last Issued Date',
    type: 'date',
    required: false,
    size: 'md',
    readOnly: true,
  },
  {
    name: 'updatedAt',
    label: 'Last Updated Date',
    type: 'date',
    required: false,
    size: 'md',
    readOnly: true,
  },
];

const STRUCTURE_META: IFormFieldV2[] = [
  {
    name: 'subtitleLabel',
    label: 'Subtitle',
    type: 'text',
    required: false,
    size: 'xl',
    validation: {maxLength: 255},
    description: 'e.g., Subtitle A',
  },
  {
    name: 'chapterLabel',
    label: 'Chapter',
    type: 'text',
    required: false,
    size: 'md',
    validation: {maxLength: 255},
    description: 'e.g., Chapter XVII',
  },
  {
    name: 'partLabel',
    label: 'Part',
    type: 'text',
    required: false,
    size: 'md',
    validation: {maxLength: 255},
    description: 'e.g., Part 1910',
  },
  {
    name: 'subpartLabel',
    label: 'Subpart',
    type: 'text',
    required: false,
    size: 'md',
    validation: {maxLength: 255},
    description: 'e.g., Subpart Z',
  },
];

const CONTENT_META: IFormFieldV2[] = [
  {
    name: 'content',
    label: '',
    type: 'richtext',
    required: false,
    size: 'xl',
  },
];

const applyReadOnly = (fields: IFormFieldV2[]): IFormFieldV2[] =>
  fields.map(f => ({...f, readOnly: true, description: undefined}));

const GetDefaultFormData = (): IFormDataV2 => ({
  sectionLabel: '',
  titleNumberLabel: '',
  issuedAt: null,
  updatedAt: null,
  subtitleLabel: '',
  chapterLabel: '',
  partLabel: '',
  subpartLabel: '',
  content: '',
});

const MapResponseToFormData = (resp: ComplianceStandardResponse): IFormDataV2 =>
  toFormDataV2({
    sectionLabel: resp.sectionLabel ?? '',
    titleNumberLabel: resp.titleNumberLabel ?? '',
    issuedAt: resp.issuedAt ?? null,
    updatedAt: resp.updatedAt ?? null,
    subtitleLabel: resp.subtitleLabel ?? '',
    chapterLabel: resp.chapterLabel ?? '',
    partLabel: resp.partLabel ?? '',
    subpartLabel: resp.subpartLabel ?? '',
    content: resp.content ?? '',
  });

export const CompanyStandardForm: React.FC<CompanyStandardFormProps> = ({
  id,
  mode,
}) => {
  const navigate = useAppNavigate();
  const {setAlert} = useAlert();
  const [isSavePending, setIsSavePending] = useState(false);
  const [loadError, setLoadError] = useState<string | undefined>();

  const perms = useModulePermissions(ModuleEnum.Compliance);

  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit';
  const isCreateMode = mode === 'create';

  const [createCustomComplianceStandard] =
    useCreateCustomComplianceStandardMutation();
  const [updateCustomComplianceStandard] =
    useUpdateCustomComplianceStandardMutation();

  const {
    data: standardData,
    isLoading,
    error: fetchError,
  } = useGetCustomComplianceStandardQuery(id!, {skip: id === undefined});

  const standardDetailsMeta = useMemo<IFormFieldV2[]>(
    () =>
      isViewMode ? applyReadOnly(STANDARD_DETAILS_META) : STANDARD_DETAILS_META,
    [isViewMode]
  );

  const structureMeta = useMemo<IFormFieldV2[]>(
    () => (isViewMode ? applyReadOnly(STRUCTURE_META) : STRUCTURE_META),
    [isViewMode]
  );

  const contentMeta = useMemo<IFormFieldV2[]>(
    () => (isViewMode ? applyReadOnly(CONTENT_META) : CONTENT_META),
    [isViewMode]
  );

  const allFormMeta = useMemo<IFormFieldV2[]>(
    () => [
      ...standardDetailsMeta,
      ...DATES_META,
      ...structureMeta,
      ...contentMeta,
    ],
    [standardDetailsMeta, structureMeta, contentMeta]
  );

  const form = useEntitySyncedForm({
    formMeta: allFormMeta,
    initialData: GetDefaultFormData(),
    entity: standardData,
    onEntityArrival: () => setLoadError(undefined),
    toFormData: MapResponseToFormData,
  });

  useEffect(() => {
    if (fetchError) {
      setLoadError(parseErrorMessage(fetchError));
    }
  }, [fetchError]);

  const sectionLabel = useWatch({
    control: form.methods.control,
    name: 'sectionLabel',
  });
  const titleNumberLabel = useWatch({
    control: form.methods.control,
    name: 'titleNumberLabel',
  });

  const handleSave = async () => {
    try {
      setIsSavePending(true);

      const values = form.methods.getValues();
      const body: CustomComplianceStandardRequest = {
        id: id,
        sectionLabel: values.sectionLabel as string,
        titleNumberLabel: values.titleNumberLabel as string | undefined,
        subtitleLabel: values.subtitleLabel as string | undefined,
        chapterLabel: values.chapterLabel as string | undefined,
        partLabel: values.partLabel as string | undefined,
        subpartLabel: values.subpartLabel as string | undefined,
        content: values.content as string | undefined,
      };

      const res =
        id === undefined
          ? await createCustomComplianceStandard(body).unwrap()
          : await updateCustomComplianceStandard(body).unwrap();

      setAlert(
        id === undefined
          ? `${values.sectionLabel} was successfully created`
          : `${values.sectionLabel} was successfully updated`,
        'success'
      );

      setIsSavePending(false);
      navigate(PathUtility.CustomComplianceStandardsView(String(res.id)), {
        replace: true,
      });
    } catch (error: unknown) {
      setAlert(parseErrorMessage(error), 'error');
      setIsSavePending(false);
    }
  };

  const handleValidateAndSave = async () => {
    const isValid = await form.validateForm();
    if (isValid) {
      void handleSave();
    }
  };

  const {goBack: handleCancel} = useBackNavigation(
    isEditMode && id
      ? PathUtility.CustomComplianceStandardsView(id)
      : PathUtility.InstanceComplianceStandards('company')
  );

  const navigation = useMemo(
    () =>
      buildPageNavigation(navigationConfig, {
        mode,
        id,
        itemName: sectionLabel as string,
      }),
    [mode, id, sectionLabel]
  );

  const saveButtons = (
    <>
      <PageHeader.Action variant="cancel" onClick={handleCancel}>
        Cancel
      </PageHeader.Action>
      <PageHeader.Action
        variant={isCreateMode ? 'create' : 'save'}
        onClick={handleValidateAndSave}
        loading={isSavePending}>
        {isCreateMode ? 'Create' : 'Save Changes'}
      </PageHeader.Action>
    </>
  );

  const backBar = (
    <BackBar
      fallbackTo={navigation.fallbackTo}
      fallbackLabel={navigation.fallbackLabel}
      breadcrumbs={navigation.breadcrumbs}
    />
  );

  if (isLoading) {
    return (
      <Stack gap="lg">
        {backBar}
        <Center py="xl">
          <Loader size="lg" />
        </Center>
      </Stack>
    );
  }

  if (loadError) {
    return (
      <Stack gap="lg">
        {backBar}
        <Alert color="red" title="Error">
          {loadError}
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack gap="lg">
      {backBar}

      <PageHeader>
        <PageHeader.Title
          icon={SidebarLinkComplianceRegulatory.icon}
          subtitle={isCreateMode ? undefined : (titleNumberLabel as string)}>
          {isCreateMode
            ? 'New Company Standard'
            : (sectionLabel as string) || 'Company Standard'}
        </PageHeader.Title>

        {isEditMode && (
          <PageHeader.Badges>
            <PageHeader.Badge color="yellow" variant="light">
              Editing
            </PageHeader.Badge>
          </PageHeader.Badges>
        )}

        <PageHeader.Actions>
          {isViewMode && perms.update && (
            <PageHeader.Action
              variant="edit"
              onClick={() =>
                navigate(PathUtility.CustomComplianceStandardsEdit(id!))
              }>
              Edit
            </PageHeader.Action>
          )}

          {!isViewMode && saveButtons}
        </PageHeader.Actions>
      </PageHeader>

      <ProactForm form={form}>
        <Stack gap="md">
          <SectionCard
            title="Standard Details"
            data-testid="section-standard-details">
            <Box p="md">
              <Grid gutter="md">
                <ProactField name="sectionLabel" />
                <ProactField name="titleNumberLabel" />
              </Grid>
            </Box>
          </SectionCard>

          {mode !== 'create' && (
            <SectionCard title="Dates" data-testid="section-dates">
              <Box p="md">
                <Grid gutter="md">
                  <ProactField name="issuedAt" />
                  <ProactField name="updatedAt" />
                </Grid>
              </Box>
            </SectionCard>
          )}

          <SectionCard title="Structure" data-testid="section-structure">
            <Box p="md">
              <Grid gutter="md">
                <ProactField name="subtitleLabel" />
                <ProactField name="chapterLabel" />
                <ProactField name="partLabel" />
                <ProactField name="subpartLabel" />
              </Grid>
            </Box>
          </SectionCard>

          <SectionCard title="Content" data-testid="section-content">
            <Box p="md">
              <Grid gutter="md">
                <ProactField name="content" />
              </Grid>
            </Box>
          </SectionCard>

          {!isViewMode && <Group mt="md">{saveButtons}</Group>}
        </Stack>
      </ProactForm>
    </Stack>
  );
};
